/**
 * Pure helpers for the settings surface — the timezone choice list + the
 * select-value ⇄ persisted-override mapping. DOM-free + i18n-free (labels are
 * applied in the component) so `bun test` exercises them directly, mirroring
 * `billing-model.ts` / `referral-model.ts`.
 */

import { isSupportedLocale, type Locale } from '@4f/i18n';
import { Theme } from '@4f/shared';

/** The select value meaning "follow auto-detection" — persists the override as `null`. */
export const FOLLOW_AUTO = 'auto';

/**
 * A curated set of common IANA zones offered in the timezone override — a full
 * ~400-zone picker is overkill for v1, and the detected zone is always added on
 * top ({@link timeZoneChoices}) so no user is ever left unable to select theirs.
 */
export const COMMON_TIME_ZONES: readonly string[] = [
  'UTC',
  'America/Los_Angeles',
  'America/Denver',
  'America/Chicago',
  'America/New_York',
  'America/Sao_Paulo',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Moscow',
  'Africa/Cairo',
  'Africa/Johannesburg',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Australia/Sydney',
  'Pacific/Auckland',
];

/**
 * The timezone choices for the override select: the auto-detected zone FIRST (so
 * it is always selectable even when it is not in the curated set), then the
 * curated common zones, de-duplicated in order.
 */
export function timeZoneChoices(detected: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const zone of [detected, ...COMMON_TIME_ZONES]) {
    if (zone && !seen.has(zone)) {
      seen.add(zone);
      out.push(zone);
    }
  }
  return out;
}

/** Prettify an IANA id for display: `America/New_York` → `America/New York`. */
export function timeZoneLabel(zone: string): string {
  return zone.replace(/_/g, ' ');
}

/** Map a theme select value to a valid {@link Theme}, defaulting to `auto` for anything unexpected. */
export function themeFromChoice(value: string): Theme {
  const parsed = Theme.safeParse(value);
  return parsed.success ? parsed.data : 'auto';
}

/**
 * Map a locale select value to a persisted override — the auto sentinel (or any
 * unsupported tag) clears it (`null` → follow auto-detection).
 */
export function localeFromChoice(value: string): Locale | null {
  return value !== FOLLOW_AUTO && isSupportedLocale(value) ? value : null;
}

/** Map a timezone select value to a persisted override — the auto sentinel clears it. */
export function timeZoneFromChoice(value: string): string | null {
  return value === FOLLOW_AUTO ? null : value;
}
